// 篩選面板（GDO-18）：桌面是頂部列下方的浮動面板，平板是右側抽屜，手機是底部抽屜。
// 條件改變立即套用（寫進網址），「完成」只是關閉。面板一直存在於頁面上（關閉時隱藏），
// 開啟時焦點移進面板、Esc 關閉並把焦點還給「篩選」按鈕；平板與手機是對話框（限制焦點、鎖住背景捲動）。
import { GENRES, type GenreKey, PLATFORMS, type PlatformKey } from '@dayone/core/dictionaries';
import { type ReactNode, type RefObject, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import {
  activeFilters,
  clearFilter,
  type FilterState,
  isPageSize,
  toggleGenre,
  togglePlatform,
} from '../../filters/state.ts';
import type { Update } from '../../filters/useFilterState.ts';
import { t } from '../../i18n/zh-TW.ts';
import { PAGE_SIZES, type SortKey } from '../../storage/prefs.ts';
import { Hint } from '../../ui/Hint.tsx';
import { ICON, X } from '../../ui/icons/index.ts';
import { PlatformIcon } from '../../ui/icons/PlatformIcon.tsx';
import { useDialog } from '../../ui/useDialog.ts';
import { StatusSelector } from './StatusSelector.tsx';

const SORTS = Object.keys(t.filters.sortOptions) as SortKey[];
const MONTH_MIN = '2000-01';
const MONTH_MAX = '2035-12';

export type PanelMode = 'popover' | 'drawer' | 'sheet';

interface Props {
  state: FilterState;
  update: (u: Update, mode?: 'push' | 'replace') => void;
  open: boolean;
  mode: PanelMode;
  onClose: () => void;
  /** 開啟面板的按鈕：浮動面板點外面關閉時不算「外面」，關閉後焦點回到這裡 */
  toggleRef: RefObject<HTMLElement | null>;
  /** 手機上放在面板底部的顯示設定（資料更新時間、外觀） */
  footer?: ReactNode;
}

export function FilterBar({ state, update, open, mode, onClose, toggleRef, footer }: Props) {
  const ref = useRef<HTMLDivElement>(null);
  const modal = mode !== 'popover';
  useDialog(ref, open && modal, onClose);
  usePopover(ref, toggleRef, open && !modal, onClose);
  const active = activeFilters(state);

  const panel = (
    <div
      ref={ref}
      id="filter-bar"
      className={`filter-bar filter-${mode}${open ? ' open' : ''}`}
      role="dialog"
      aria-modal={modal || undefined}
      aria-labelledby="filter-title"
      tabIndex={-1}
      hidden={!open}
    >
      <div className="fp-head">
        <h2 id="filter-title">{t.filters.panel}</h2>
        <button type="button" className="icon-btn" aria-label={t.detail.close} onClick={onClose}>
          <X {...ICON} />
        </button>
      </div>

      <div className="fp-body">
        <section className="fp-section">
          <StatusSelector value={state.status} onChange={(v) => update({ status: v, page: 1 })} />
        </section>

        <fieldset className="fp-section filter-group">
          <legend className="control-label">
            <Hint text={t.filters.platforms} content={t.filters.platformsHint} />
          </legend>
          <div className="chips fp-platforms">
            {PLATFORMS.map(({ key }) => {
              const on = state.platforms.includes(key);
              return (
                <button
                  key={key}
                  type="button"
                  className={on ? 'chip chip-on' : 'chip'}
                  aria-pressed={on}
                  onClick={() => update({ platforms: togglePlatform(state.platforms, key as PlatformKey), page: 1 })}
                >
                  <PlatformIcon platform={key as PlatformKey} />
                  {t.platforms[key]}
                </button>
              );
            })}
          </div>
        </fieldset>

        <fieldset className="fp-section filter-group">
          <legend className="control-label">
            <Hint text={t.filters.genres} content={t.filters.genresHint} />
          </legend>
          <div className="chips fp-genres">
            {GENRES.map(({ key }) => {
              const on = state.genres.includes(key);
              return (
                <button
                  key={key}
                  type="button"
                  className={on ? 'chip chip-on' : 'chip'}
                  aria-pressed={on}
                  onClick={() => update({ genres: toggleGenre(state.genres, key as GenreKey), page: 1 })}
                >
                  {t.genres[key]}
                </button>
              );
            })}
          </div>
        </fieldset>

        <fieldset className="fp-section filter-group date-range">
          <legend className="control-label">
            <Hint text={t.filters.dates} content={t.filters.dateHint} />
          </legend>
          <label>
            <span className="control-label">{t.filters.dateFrom}</span>
            <input
              type="month"
              min={MONTH_MIN}
              max={MONTH_MAX}
              value={state.from ?? ''}
              onChange={(e) => update({ from: e.target.value || null, page: 1 }, 'replace')}
            />
          </label>
          <label>
            <span className="control-label">{t.filters.dateTo}</span>
            <input
              type="month"
              min={MONTH_MIN}
              max={MONTH_MAX}
              value={state.to ?? ''}
              onChange={(e) => update({ to: e.target.value || null, page: 1 }, 'replace')}
            />
          </label>
        </fieldset>

        <div className="fp-section fp-row">
          <div className="filter-group">
            <span className="control-label">
              <Hint text={t.filters.sort} content={t.filters.sortHint} />
            </span>
            <select
              aria-label={t.filters.sort}
              value={state.sort}
              onChange={(e) => update({ sort: e.target.value as SortKey, page: 1 })}
            >
              {SORTS.map((s) => (
                <option key={s} value={s}>
                  {t.filters.sortOptions[s]}
                </option>
              ))}
            </select>
          </div>

          <fieldset className="filter-group">
            <legend className="control-label">
              <Hint text={t.filters.view} content={t.filters.viewHint} />
            </legend>
            <div className="segmented">
              {(['scroll', 'pages'] as const).map((v) => (
                <button
                  key={v}
                  type="button"
                  aria-pressed={state.view === v}
                  className={state.view === v ? 'seg seg-on' : 'seg'}
                  onClick={() => update({ view: v, page: 1 })}
                >
                  {v === 'scroll' ? t.filters.viewScroll : t.filters.viewPages}
                </button>
              ))}
            </div>
            {state.view === 'pages' && (
              <label className="page-size">
                <span className="sr-only">{t.filters.pageSize}</span>
                <select
                  value={state.pageSize}
                  onChange={(e) => {
                    const n = Number(e.target.value);
                    if (isPageSize(n)) update({ pageSize: n, page: 1 });
                  }}
                >
                  {PAGE_SIZES.map((n) => (
                    <option key={n} value={n}>
                      {t.filters.pageSize} {n}
                    </option>
                  ))}
                </select>
              </label>
            )}
          </fieldset>
        </div>

        {footer && <div className="filter-footer">{footer}</div>}
      </div>

      <div className="fp-foot">
        <button
          type="button"
          className="btn btn-ghost"
          disabled={active.length === 0}
          onClick={() => update((s) => clearFilter(s, 'all'))}
        >
          {active.length > 0 ? `${t.filters.reset}（${t.filters.active(active.length)}）` : t.filters.reset}
        </button>
        <button type="button" className="btn btn-primary" onClick={onClose}>
          {t.filters.done}
        </button>
      </div>
    </div>
  );

  return createPortal(
    <>
      {modal && open && <div className="fp-scrim" onClick={onClose} aria-hidden="true" />}
      {panel}
    </>,
    document.body,
  );
}

/**
 * 浮動面板（非對話框）：開啟時焦點移進面板；Esc 或點面板與按鈕以外的地方關閉，
 * Esc 關閉時焦點回到開啟按鈕。
 */
function usePopover(
  ref: RefObject<HTMLElement | null>,
  toggleRef: RefObject<HTMLElement | null>,
  open: boolean,
  onClose: () => void,
) {
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  useEffect(() => {
    if (!open) return;
    const root = ref.current;
    root?.querySelector<HTMLElement>('button, input, select')?.focus({ preventScroll: true });
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      // 說明浮窗開著時先關說明（Hint 自己處理），這裡只在焦點在面板內時關閉面板
      if (!root?.contains(document.activeElement)) return;
      e.preventDefault();
      onCloseRef.current();
      toggleRef.current?.focus();
    };
    const onDown = (e: PointerEvent) => {
      // 轉場進行中點到的是轉場圖層（html），不算點面板外面
      if (document.documentElement.dataset.vt) return;
      const target = e.target as Node;
      if (root?.contains(target) || toggleRef.current?.contains(target)) return;
      // 說明浮窗（掛在 body 上）裡的點擊不算外面
      if ((target as Element).closest?.('[role="tooltip"]')) return;
      onCloseRef.current();
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('pointerdown', onDown);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('pointerdown', onDown);
    };
  }, [open, ref, toggleRef]);
}
